import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { FaStar, FaSpinner } from 'react-icons/fa';
import { UnifiedSpotlightModal } from './UnifiedSpotlightModal';
import { usePostSpotlight } from '../hooks/usePostSpotlight';
import { useAppDispatch, useAppSelector } from '@/redux/Store';
import { fetchUserBalance } from '@/redux/thunks/marketplaceThunks';
import { toast } from 'react-toastify';
import './PostSpotlightButton.scss';

interface PostSpotlightButtonProps {
  postId: string;
  onSuccess?: (duration: number) => void;
  durationHours?: number; // Default duration
  className?: string;
}

/**
 * Reusable button component for spotlighting posts
 * Streamlined flow: click -> select duration & see price -> confirm
 */
export const PostSpotlightButton: React.FC<PostSpotlightButtonProps> = ({
  postId,
  onSuccess,
  durationHours = 24, // Default 24 hours
  className = '',
}) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const isEmailUser = user?.authMethod === 'email';

  const [showModal, setShowModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [spotlightError, setSpotlightError] = useState<string | null>(null);

  const {
    prepareTransaction,
    executeTransaction,
    recordPurchase,
    purchaseSpotlight,
    reset,
  } = usePostSpotlight();

  /**
   * User clicks spotlight button
   */
  const handleSpotlightClick = () => {
    setShowModal(true);
  };

  /**
   * Load price when duration changes
   */
  const handleDurationChange = async (duration: number): Promise<{
    price: number;
    canProceed?: boolean;
    warning?: string;
    balance?: { current: number; required: string; shortfall: string | null };
  }> => {
    try {
      const details = await prepareTransaction({ postId, durationHours: duration });
      return {
        price: parseFloat(details.totalCost) || 0,
        canProceed: details.canProceed,
        warning: details.warning,
        balance: details.balance,
      };
    } catch (err: any) {
      console.error('Failed to get price:', err);
      throw err;
    }
  };

  /**
   * User confirms spotlight purchase
   */
  const handleConfirm = async (duration: number) => {
    setIsProcessing(true);

    try {
      if (isEmailUser) {
        // Email users: Backend signs and executes transaction
        toast.info('Processing spotlight purchase...');
        await purchaseSpotlight({ postId, durationHours: duration });
      } else {
        // Wallet users: Sign with Klever Web Extension
        toast.info('Please sign the transaction in your Klever wallet...');
        const txHash = await executeTransaction();

        // Record purchase in database
        toast.info('Recording purchase...');
        await recordPurchase(txHash);
      }

      // Refresh user balance — spotlight spend is debited on chain but no
      // thunk in the spotlight flow refetches the displayed balance.
      dispatch(fetchUserBalance());

      // Close spotlight modal
      setShowModal(false);
      reset();

      // Call parent success callback (parent will show success modal)
      if (onSuccess) {
        onSuccess(duration);
      }
    } catch (err: any) {
      const message = err.message || 'Failed to spotlight post';
      setSpotlightError(message);
      toast.error(message);
      console.error('Spotlight error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * User cancels
   */
  const handleCancel = () => {
    setShowModal(false);
    setSpotlightError(null);
    reset();
  };

  return (
    <>
      <button
        className={`spotlight-btn ${className}`}
        onClick={handleSpotlightClick}
        title="Spotlight this post"
        disabled={isProcessing}
      >
        {isProcessing ? (
          <>
            <FaSpinner className="spinner" /> <span className="spotlight-btn__label">Processing...</span>
          </>
        ) : (
          <>
            <FaStar /> <span className="spotlight-btn__label">Spotlight</span>
          </>
        )}
      </button>

      {/* Unified Spotlight Modal - Rendered via Portal */}
      {ReactDOM.createPortal(
        <UnifiedSpotlightModal
          isOpen={showModal}
          title="Spotlight Post"
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          onDurationChange={handleDurationChange}
          isProcessing={isProcessing}
          defaultDuration={durationHours}
          submissionError={spotlightError}
          onClearError={() => setSpotlightError(null)}
          explainer="Spotlighted posts get pinned to the top of the feed for everyone in the same state as the author — for the duration you select. The badge clears automatically when the timer runs out."
        />,
        document.body
      )}
    </>
  );
};

export default PostSpotlightButton;
